/** Minimal shape shared by canvas ImageData and test fixtures */
export interface PixelImage {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

export type Rgb = [number, number, number];

/** Max per-channel distance from the background colour for a pixel to count as background */
const TOLERANCE = 24;
/** Pixels on the cut edge within this distance get partial alpha so anti-aliased edges don't leave a fringe */
const FEATHER = 96;
/** Share of border pixels that must match the dominant colour for the background to count as solid */
const MIN_MATCH_RATIO = 0.9;

function distance(data: Uint8ClampedArray, i: number, [r, g, b]: Rgb) {
  return Math.max(Math.abs(data[i] - r), Math.abs(data[i + 1] - g), Math.abs(data[i + 2] - b));
}

function borderIndexes({ width, height }: PixelImage) {
  const idx: number[] = [];
  for (let x = 0; x < width; x++) {
    idx.push(x * 4, ((height - 1) * width + x) * 4);
  }
  for (let y = 1; y < height - 1; y++) {
    idx.push(y * width * 4, (y * width + width - 1) * 4);
  }

  return idx;
}

/**
 * Returns the background colour when the image border is one solid, fully opaque colour.
 * Returns null for gradients, photos, or images that already have transparency.
 */
export function detectSolidBackground(img: PixelImage): Rgb | null {
  if (img.width < 2 || img.height < 2) {
    return null;
  }
  const { data } = img;
  const border = borderIndexes(img);

  // Mode colour, quantised to 5 bits per channel so JPEG noise lands in one bucket
  const buckets = new Map<number, { count: number; sum: Rgb }>();
  for (const i of border) {
    if (data[i + 3] < 255) {
      return null;
    }
    const key = ((data[i] >> 3) << 10) | ((data[i + 1] >> 3) << 5) | (data[i + 2] >> 3);
    const bucket = buckets.get(key) ?? { count: 0, sum: [0, 0, 0] };
    bucket.count++;
    bucket.sum[0] += data[i];
    bucket.sum[1] += data[i + 1];
    bucket.sum[2] += data[i + 2];
    buckets.set(key, bucket);
  }
  const mode = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
  const color: Rgb = [
    Math.round(mode.sum[0] / mode.count),
    Math.round(mode.sum[1] / mode.count),
    Math.round(mode.sum[2] / mode.count),
  ];

  const matching = border.filter(i => distance(data, i, color) <= TOLERANCE).length;

  return matching / border.length >= MIN_MATCH_RATIO ? color : null;
}

/**
 * Makes every background-coloured pixel connected to the image border transparent.
 * Mutates and returns img. Enclosed regions of the same colour are left alone.
 */
export function removeBackground(img: PixelImage, color: Rgb): PixelImage {
  const { width, height, data } = img;
  const pixelCount = width * height;
  const removed = new Uint8Array(pixelCount);
  const stack = borderIndexes(img).map(i => i / 4);

  while (stack.length) {
    const p = stack.pop();
    if (removed[p] || distance(data, p * 4, color) > TOLERANCE) {
      continue;
    }

    removed[p] = 1;
    data[p * 4 + 3] = 0;

    const x = p % width;
    if (x > 0) {
      stack.push(p - 1);
    }
    if (x < width - 1) {
      stack.push(p + 1);
    }
    if (p >= width) {
      stack.push(p - width);
    }
    if (p < pixelCount - width) {
      stack.push(p + width);
    }
  }

  // One pixel feather along the cut edge
  for (let p = 0; p < pixelCount; p++) {
    if (removed[p]) {
      continue;
    }
    const x = p % width;
    const touchesRemoved = (x > 0 && removed[p - 1])
      || (x < width - 1 && removed[p + 1])
      || (p >= width && removed[p - width])
      || (p < pixelCount - width && removed[p + width]);
    if (!touchesRemoved) {
      continue;
    }
    const d = distance(data, p * 4, color);
    if (d < FEATHER) {
      data[p * 4 + 3] = Math.min(data[p * 4 + 3], Math.round((255 * d) / FEATHER));
    }
  }

  return img;
}

/** Crops away fully transparent rows and columns around the image. Returns img unchanged if nothing is opaque. */
export function trimTransparent(img: PixelImage): PixelImage {
  const { width, height, data } = img;
  let top = height;
  let left = width;
  let bottom = -1;
  let right = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] === 0) {
        continue;
      }

      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
      left = Math.min(left, x);
      right = Math.max(right, x);
    }
  }

  if (bottom < 0) {
    return img;
  }

  const outWidth = right - left + 1;
  const outHeight = bottom - top + 1;
  const out = new Uint8ClampedArray(outWidth * outHeight * 4);

  for (let y = 0; y < outHeight; y++) {
    const start = ((top + y) * width + left) * 4;
    out.set(data.subarray(start, start + outWidth * 4), y * outWidth * 4);
  }

  return { width: outWidth, height: outHeight, data: out };
}

/** Decodes an image file into raw pixels via an offscreen canvas. Browser only. */
export async function loadPixels(file: Blob): Promise<ImageData> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement(`canvas`);
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext(`2d`);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Encodes raw pixels as a PNG blob. Browser only. */
export function pixelsToPng(img: PixelImage): Promise<Blob> {
  const canvas = document.createElement(`canvas`);
  canvas.width = img.width;
  canvas.height = img.height;
  canvas.getContext(`2d`).putImageData(new ImageData(img.data as Uint8ClampedArray<ArrayBuffer>, img.width, img.height), 0, 0);

  return new Promise((resolve, reject) => canvas.toBlob(
    blob => (blob ? resolve(blob) : reject(new Error(`Could not encode image`))),
    `image/png`,
  ));
}
